import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { Button } from "../ui/button";
import { setModal, ModalType } from "@/store/globalSettingsSlice";
import { JsxElementType, JsxElement } from "@/utils/jsx/jsxElementTypes";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCreateComponentModalStore } from "@/store/zustand/createComponentModalStore";

interface CreateComponentSectionProps {
  selectedCodebaseId?: string | null;
}

export const CreateComponentSection: React.FC<CreateComponentSectionProps> =
  React.memo(({ selectedCodebaseId }) => {
    const treeElementLookup = useCodebaseStore((s) => s.treeElementLookup);
    const selectedTreeElement = useMemo(
      () => treeElementLookup[selectedCodebaseId || ""],
      [treeElementLookup, selectedCodebaseId],
    );

    const dispatch = useDispatch();

    // Check if the selected element is eligible for creating a component
    // Based on the same logic as CanvasContextMenu
    const isEligibleForComponentCreation =
      selectedTreeElement &&
      selectedTreeElement.type === JsxElementType.Element &&
      selectedTreeElement.codebaseId; // Similar to isKnownElement() check

    const handleCreateClick = () => {
      if (!isEligibleForComponentCreation) {
        // Show modal to suggest right-clicking on an element
        dispatch(setModal(ModalType.CreateComponent));
        return;
      }

      // Open the modal via Zustand store
      useCreateComponentModalStore.getState().openModal({
        codebaseId: selectedTreeElement.codebaseId,
        filename: selectedTreeElement.filename,
        jsxName: selectedTreeElement.jsxName,
        contentsStart: selectedTreeElement.contentsStart,
        contentsEnd: selectedTreeElement.contentsEnd,
      });
    };

    // Don't render the section if no eligible element is selected
    if (!isEligibleForComponentCreation) {
      return null;
    }

    return (
      <>
        <div className="px-4 py-2">
          <div className="flex flex-row justify-between items-center">
            <div className="flex items-center gap-2 text-xs font-medium text-secondary-n-400">
              {selectedTreeElement?.jsxName || "Select element"}
            </div>
            <button
              className="p-1 hover:bg-secondary-300 rounded transition-colors"
              onClick={handleCreateClick}
              title="Create component"
            >
              <img
                src="/icons/component.svg"
                alt="Create component"
                className="w-4 h-4 opacity-70 hover:opacity-100 transition-opacity"
                style={{ filter: "invert(1)" }} // Makes the black icon white for dark theme
              />
            </button>
          </div>
        </div>
        <hr />
      </>
    );
  });
